/**
 * Relationship-to-deceased options shared across contact, intake, and review UIs.
 *
 * Values are stable identifiers consumed by backend features (e.g. intestacy
 * prefill) — do not rename without a backend migration.
 */

/**
 * **The list is a vocabulary, not a constraint.** `contact_matters.relationship`
 * is a nullable `varchar(50)` with no enum and no DTO validation, and production
 * already holds ~6% of values from outside this list — `charity` (47 rows),
 * `mother`, `stepchild`, `financial adviser`, `carer`, `health attendant`. The
 * picker is therefore a combobox that also accepts free text
 * (`RelationshipCombobox`); these entries are the spellings we recognise,
 * inflect and can prefill a discovery letter type from.
 *
 * The professional and organisation entries below were added because lawyers
 * were already typing them, and because a contact recorded only as "Professional
 * advisor" loses which kind — a fact a discovery letter cares about.
 * `professional-advisor` is kept as the catch-all for the ones not named here,
 * and because production rows already carry it.
 */
export const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse / partner' },
  { value: 'child', label: 'Child' },
  { value: 'step-child', label: 'Step-child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'grandchild', label: 'Grandchild' },
  { value: 'other-relative', label: 'Other relative' },
  { value: 'friend', label: 'Friend' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'former-solicitor', label: 'Former solicitor' },
  { value: 'financial-adviser', label: 'Financial adviser' },
  { value: 'professional-advisor', label: 'Professional advisor' },
  { value: 'former-employer', label: 'Former employer' },
  { value: 'charity', label: 'Charity' },
  { value: 'other', label: 'Other' }
] as const;

export const RELATIONSHIP_LABELS: Record<string, string> = Object.fromEntries(
  RELATIONSHIP_OPTIONS.map((o) => [o.value, o.label])
);

/**
 * Inverse relationship map: given the recipient's relationship *to* the
 * deceased, how the copy should refer to the **deceased** (e.g. a "child"
 * recipient → the deceased is their "parent"). Neutral (ungendered) words —
 * this surface has no title/gender signal.
 *
 * The backend mirrors this (gendered) in
 * `backend/src/intake-forms/utils/deceased-relationship.utils.ts` for the
 * invitation email; frontend and backend share no package, so keep the two in
 * sync by hand. `other` is intentionally omitted — an "Other" relationship has
 * no confident inverse, so callers fall back to their own generic wording.
 */
export const DECEASED_RELATIONSHIP_LABELS: Record<string, string> = {
  spouse: 'partner',
  child: 'parent',
  'step-child': 'step-parent',
  parent: 'child',
  sibling: 'sibling',
  grandchild: 'grandparent',
  'other-relative': 'relative',
  friend: 'friend',
  // Every professional the deceased engaged saw them as a client, so the four
  // share one inverse; only which professional they were differs, and that is
  // what the relationship value itself carries.
  accountant: 'client',
  'former-solicitor': 'client',
  'financial-adviser': 'client',
  'professional-advisor': 'client',
  'former-employer': 'employee'
  // `charity` is deliberately absent, alongside `other`: a charity's inverse
  // would be "benefactor", which is a word about a bequest rather than about
  // the deceased, and no charity is ever sent an intake invitation anyway.
};

/**
 * The **gendered** inverse — the word the intake-invitation email actually
 * prints, given the enquirer's relationship and the deceased's title.
 *
 * A mirror of `backend/src/intake-forms/utils/deceased-relationship.utils.ts`
 * (`INVERSE_RELATIONSHIP_WORDS`), which is what renders the sent email; frontend
 * and backend share no package, so **keep the two in sync by hand**. The neutral
 * map above is the same inversion without a gender signal, for surfaces that
 * have no title to read — this one exists because a surface that *collects* the
 * title has to be able to show what the title changes.
 *
 * `other` maps to `null` deliberately: an "Other" enquirer could be estranged or
 * anything at all, so the backend presumes no word and keeps the template's
 * review marker instead.
 */
const GENDERED_DECEASED_RELATIONSHIP_WORDS: Record<
  string,
  { male: string; female: string; neutral: string } | undefined
> = {
  spouse: { male: 'husband', female: 'wife', neutral: 'partner' },
  child: { male: 'father', female: 'mother', neutral: 'parent' },
  'step-child': {
    male: 'step-father',
    female: 'step-mother',
    neutral: 'step-parent'
  },
  parent: { male: 'son', female: 'daughter', neutral: 'child' },
  sibling: { male: 'brother', female: 'sister', neutral: 'sibling' },
  grandchild: {
    male: 'grandfather',
    female: 'grandmother',
    neutral: 'grandparent'
  },
  'other-relative': {
    male: 'relative',
    female: 'relative',
    neutral: 'relative'
  },
  friend: { male: 'friend', female: 'friend', neutral: 'friend' },
  // Ungendered, like `friend` and `other-relative` — see the neutral map above
  // for why the four professionals share one inverse and why `charity` has none.
  accountant: { male: 'client', female: 'client', neutral: 'client' },
  'former-solicitor': { male: 'client', female: 'client', neutral: 'client' },
  'financial-adviser': { male: 'client', female: 'client', neutral: 'client' },
  'professional-advisor': {
    male: 'client',
    female: 'client',
    neutral: 'client'
  },
  'former-employer': {
    male: 'employee',
    female: 'employee',
    neutral: 'employee'
  }
};

/**
 * The honorifics a title picker offers, as `value`/`label` pairs.
 *
 * **The single shared vocabulary**, so the deceased's title is chosen from the
 * same list whether it is captured on an enquiry or on a matter contact. (It was
 * previously kept in step with an inline JSON-schema `enum` in the RJSF contact
 * form; that form was never reachable and was retired with the rest of RJSF in
 * LF-6211, leaving this as the only definition.)
 *
 * `''` is deliberately **not** a member. A surface that allows "no title" renders
 * its own blank entry with its own wording, because what a blank means differs by
 * surface: on the lead invitation it means the email keeps its hand-fill marker.
 * There is likewise **no `Other`**: storing the literal string `Other` as
 * somebody's title renders as *"your late Other"*, which is worse than no title
 * at all.
 */
export const DECEASED_TITLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
  { value: 'Rev', label: 'Rev' },
  { value: 'Hon', label: 'Hon' }
];

/**
 * Derive a gender proxy from a title. Mr → male, Mrs/Ms/Miss → female,
 * everything else (Dr, Mx, Prof, blank, unknown) → neutral.
 *
 * Normalises case and punctuation so `Mr`, `mr` and `Mr.` all resolve, mirroring
 * the backend's own `genderFromTitle`. That tolerance was originally the argument
 * for collecting the title as free text; it is now the argument for why a picker
 * is safe (LF-6054, owner call at QA). A title outside
 * {@link DECEASED_TITLE_OPTIONS} still degrades to neutral rather than being
 * rejected, so historical rows and PMS imports keep working unchanged.
 */
export function genderFromTitle(
  title: string | null | undefined
): 'male' | 'female' | 'neutral' {
  const normalised = (title ?? '').toLowerCase().replace(/[^a-z]/g, '');
  if (normalised === 'mr') return 'male';
  if (normalised === 'mrs' || normalised === 'ms' || normalised === 'miss') {
    return 'female';
  }
  return 'neutral';
}

/**
 * How the invitation email will refer to the deceased — the read-out a surface
 * collecting these fields shows so the lawyer can check them.
 *
 * Returns `null` when no word can be determined (no relationship, or an "Other"
 * one), which is the honest answer: the email keeps its hand-fill marker in that
 * case rather than inventing a word.
 */
export function deceasedRelationshipWord(
  relationship: string | null | undefined,
  title: string | null | undefined
): string | null {
  if (!relationship) return null;
  const entry = GENDERED_DECEASED_RELATIONSHIP_WORDS[relationship];
  if (!entry) return null;
  return entry[genderFromTitle(title)];
}
